import { useState } from "react";
import { api, adminSession } from "@/lib/api";
import { toast } from "sonner";
import { Key, AlertTriangle } from "lucide-react";
import TotpCard from "@/components/admin/TotpCard";

// Extracted from /pages/SiteAdmin.jsx in iter84.40 (tech debt sweep #4 batch 1).
// Pure UI component — no behavior changes vs. the inline version. Owns its
// own profile-edit state; relies on the parent for the auth'd `admin`
// object, the `onUpdated` callback (so the parent can refresh `admin`
// state after a save), and `onOpenChangePassword` to hand control back
// to the parent's modal.
export default function AccountTab({ admin, onUpdated, onOpenChangePassword }) {
    const [email, setEmail] = useState(admin.email || "");
    const [name, setName] = useState(admin.name || "");
    const [saving, setSaving] = useState(false);

    const dirty = email.trim().toLowerCase() !== (admin.email || "").toLowerCase()
        || name.trim() !== (admin.name || "");

    const handleSave = async () => {
        const body = {};
        if (email.trim().toLowerCase() !== (admin.email || "").toLowerCase()) body.email = email.trim().toLowerCase();
        if (name.trim() !== (admin.name || "")) body.name = name.trim();
        setSaving(true);
        try {
            const updated = await api.authUpdateMe(body);
            toast.success("Profile updated");
            onUpdated(updated);
            adminSession.save(null, updated);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        }
        setSaving(false);
    };

    return (
        <div className="brutal-card p-5 space-y-5 max-w-lg" data-testid="account-tab-card">
            <div>
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">Signed in as</p>
                <p className="font-display font-extrabold text-xl">{admin.email}</p>
                <p className="text-xs text-zinc-500">{admin.is_super ? "Super Admin" : "Admin"}</p>
            </div>

            <div className="space-y-3 pt-3 border-t-2 border-[#222]">
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight">Profile</h3>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email</label>
                    <input
                        data-testid="account-email-input"
                        type="email" value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="brutal-input w-full"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Display name</label>
                    <input
                        data-testid="account-name-input"
                        type="text" value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        className="brutal-input w-full"
                    />
                </div>
                <button
                    data-testid="account-save-profile-btn"
                    onClick={handleSave}
                    disabled={!dirty || saving}
                    className="brutal-btn-secondary disabled:opacity-60"
                >
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>

            <div className="pt-3 border-t-2 border-[#222]">
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight mb-2">Security</h3>
                <button data-testid="open-change-password-btn" onClick={onOpenChangePassword} className="brutal-btn-primary">
                    <Key className="w-4 h-4" /> Change password
                </button>
                <div className="brutal-card-light p-3 text-xs text-zinc-400 flex items-start gap-2 mt-3">
                    <AlertTriangle className="w-4 h-4 text-blaze flex-shrink-0 mt-0.5" />
                    <span>Pick a strong password and update your email above if you want password-reset links sent to a different inbox.</span>
                </div>
                <TotpCard />
            </div>
        </div>
    );
}
